import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { EditorState, convertToRaw, ContentState, Modifier, SelectionState } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';

interface ControlledEditorProps {
  initialValue: string;
  onChange: () => unknown;
}

const ControlledEditor: React.FC<ControlledEditorProps > = forwardRef(({onChange, initialValue}, ref) => {
  const editorRef = useRef(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [currentSelection, setCurrentSelection] = useState(null);

  useImperativeHandle(ref, () => ({
    updateBody(text: string) {
     const contentBlock = htmlToDraft(text);
     if (text&&contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        setEditorState(EditorState.createWithContent(contentState));
     }
    },
    pushVar(text: string) {
      const editorState = editorRef.current?.getEditorState();
      if (!editorState) return;
      const contentState = editorState.getCurrentContent();
      const newContentState = Modifier.insertText(
        contentState,
        currentSelection,
        text
      );
      const newEditorState = EditorState.push(
        editorState,
        newContentState,
        'insert-characters'
      );
      setEditorState(newEditorState);
    },
  }));

 const handleCursorEvent = () => {
    const selectionState = editorState.getSelection();
    setCurrentSelection(selectionState);
  };

  useEffect(() => {
    if (onChange && editorState && editorState.getCurrentContent) {
      const text = draftToHtml(convertToRaw(editorState.getCurrentContent()));
      onChange(text);
    }
  }, [editorState]);

  useEffect(() => {
    if (initialValue) {
      const contentBlock = htmlToDraft(initialValue);
      if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        const initialEditorState = EditorState.createWithContent(contentState);
        setEditorState(initialEditorState);
      }
    }
  }, []);

  const onEditorStateChange = (newEditorState) => {
    setEditorState(newEditorState);
  };

  return (
    <Editor
      ref={editorRef}
      editorState={editorState}
      wrapperClassName="demo-wrapper"
      editorClassName="demo-editor"
      onEditorStateChange={onEditorStateChange}
      onBlur={handleCursorEvent}
      onFocus={handleCursorEvent}
      toolbar={{
          options: ['colorPicker', 'link', 'embedded', 'image', 'inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign'],
          inline: {
            options: ['bold', 'italic', 'underline', 'strikethrough'],
          },
          blockType: {
            options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote'],
          },
          colorPicker: {
            popupClassName: 'color-popup',
            portalTarget: document.body,
          },
        }}
    />
  );
});

export default ControlledEditor;